import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import ProtectedRoute from "@/components/ProtectedRoute";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import AuthCallback from "@/pages/AuthCallback";
import Profile from "@/pages/Profile";
import CVs from "@/pages/CVs";
import TailorCV from "@/pages/TailorCV";
import ApplyFlow from "@/pages/ApplyFlow";
import AutoApply from "@/pages/AutoApply";
import Screening from "@/pages/Screening";
import Tracker from "@/pages/Tracker";
import Settings from "@/pages/Settings";
import FreeCvAnalysis from "@/pages/FreeCvAnalysis";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import AdminLeads from "@/pages/AdminLeads";
import AdminLlmSettings from "@/pages/AdminLlmSettings";
import AdminAffiliates from "@/pages/AdminAffiliates";
import AdminLayout from "@/pages/admin/AdminLayout";
import AdminOverview from "@/pages/admin/AdminOverview";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminSubscriptions from "@/pages/admin/AdminSubscriptions";
import AdminPromotions from "@/pages/admin/AdminPromotions";
import AdminReferrals from "@/pages/admin/AdminReferrals";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminAudit from "@/pages/admin/AdminAudit";
import AdminMatcher from "@/pages/admin/AdminMatcher";
import AdminLlmUsage from "@/pages/admin/AdminLlmUsage";
import AdminGdpr from "@/pages/admin/AdminGdpr";
import AdminEmail from "@/pages/admin/AdminEmail";
import AdminLinks from "@/pages/admin/AdminLinks";
import Welcome from "@/pages/Welcome";
import Billing from "@/pages/Billing";
import Support from "@/pages/Support";
import PaymentSuccess from "@/pages/PaymentSuccess";
import VerifyEmail from "@/pages/VerifyEmail";
import Referrals from "@/pages/Referrals";
import JobMatches from "@/pages/JobMatches";
import LinkedInOptimize from "@/pages/LinkedInOptimize";
import NhsSupportingInfo from "@/pages/NhsSupportingInfo";
import AdminSponsorReviews from "@/pages/admin/AdminSponsorReviews";
import AdminPayoutWaitlist from "@/pages/admin/AdminPayoutWaitlist";
import Partners from "@/pages/Partners";
import PartnerDashboard from "@/pages/PartnerDashboard";
import BrandWatermark from "@/components/BrandWatermark";
import { useEffect } from "react";
import { captureAff } from "@/lib/affiliate";

function RootRedirect() {
  const { user, loading } = useAuth();
  if (loading || user === null) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (user.is_admin) return <Navigate to="/admin" replace />;
  if (user.is_affiliate) return <Navigate to="/partners/dashboard" replace />;
  return <Navigate to="/profile" replace />;
}

function AppRoutes() {
  const location = useLocation();
  useEffect(() => { captureAff(location.search); }, [location.search]);
  // Handle Google OAuth callback synchronously before anything else
  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  return (
    <Routes>
      <Route path="/" element={<RootRedirect />} />
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/analyse" element={<FreeCvAnalysis />} />
      <Route path="/partners" element={<Partners />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route path="/profile" element={<Profile />} />
        <Route path="/apply" element={<ApplyFlow />} />
        <Route path="/auto-apply" element={<AutoApply />} />
        <Route path="/cvs" element={<CVs />} />
        <Route path="/tailor" element={<TailorCV />} />
        <Route path="/screening" element={<Screening />} />
        <Route path="/tracker" element={<Tracker />} />
        <Route path="/linkedin" element={<LinkedInOptimize />} />
        <Route path="/nhs-supporting" element={<NhsSupportingInfo />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/welcome" element={<Welcome />} />
        <Route path="/support" element={<Support />} />
        <Route path="/billing" element={<Billing />} />
        <Route path="/referrals" element={<Referrals />} />
        <Route path="/matches" element={<JobMatches />} />
        <Route path="/partners/dashboard" element={<PartnerDashboard />} />
        <Route path="/payment/success" element={<PaymentSuccess />} />
        <Route path="/admin" element={<AdminLayout />}>
          <Route index element={<AdminOverview />} />
          <Route path="users" element={<AdminUsers />} />
          <Route path="subscriptions" element={<AdminSubscriptions />} />
          <Route path="promotions" element={<AdminPromotions />} />
          <Route path="affiliates" element={<AdminAffiliates />} />
          <Route path="referrals" element={<AdminReferrals />} />
          <Route path="leads" element={<AdminLeads />} />
          <Route path="matcher" element={<AdminMatcher />} />
          <Route path="sponsor-reviews" element={<AdminSponsorReviews />} />
          <Route path="payout-waitlist" element={<AdminPayoutWaitlist />} />
          <Route path="llm" element={<AdminLlmSettings />} />
          <Route path="llm-usage" element={<AdminLlmUsage />} />
          <Route path="email" element={<AdminEmail />} />
          <Route path="gdpr" element={<AdminGdpr />} />
          <Route path="audit" element={<AdminAudit />} />
          <Route path="links" element={<AdminLinks />} />
          <Route path="settings" element={<AdminSettings />} />
        </Route>
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
        <Toaster position="bottom-right" richColors />
        <BrandWatermark />
      </AuthProvider>
    </div>
  );
}

export default App;
